import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Operation, StorageService } from '@factor_ec/utils';
import { MessageService } from '@factor_ec/ui';

import { Payment, PaymentType } from '@/main/models/payment';
import { PaymentComponent } from '@/main/components/payment/payment.component';
import { Credit } from '@/main/models/credit';
import { ApiService } from '@/core/api.service';
import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';

/**
 * @description Servicio para gestionar operaciones de pago
 * @class PaymentService
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  // Dependency injection
  private readonly apiService = inject(ApiService);
  private readonly appService = inject(AppService);
  private readonly storageService = inject(StorageService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly sessionService = inject(SessionService);

  // Events
  public readonly change: EventEmitter<Operation> = new EventEmitter();

  /**
   * @description Abre el diálogo de pago
   * @param {Object} [data] - Datos para el diálogo de pago
   * @param {PaymentType} [data.type] - Tipo de pago
   * @param {Credit} data.credit - Crédito asociado al pago
   * @param {Partial<Payment>} [data.payment] - Datos del pago existente
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  public async open(data?: {
    type?: PaymentType;
    credit: Credit;
    payment?: Partial<Payment>;
  }): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(PaymentComponent, {
        data,
        panelClass: 'ft-dialog',
        width: '400px'
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Revierte un pago realizado
   * @param {Credit} credit - Crédito asociado al pago
   * @param {Payment} payment - Pago a revertir
   * @returns {Promise<void>} Promesa que resuelve cuando se completa la reversión
   */
  public async reverse(credit: Credit, payment: Payment): Promise<Operation | null> {
    if (credit.status.id === 'CAN' && !(await this.allowNewCredit(credit))) {
      return null;
    }

    return new Promise<Operation>((resolve, reject) => {
      this.messageService
        .show(
          $localize`Are you sure you want to reverse payment #${payment.ticketNumber}?`,
          {
            type: 'modal',
            class: 'text-center d-flex flex-column align-items-center gap-3',
            icon: {
              name: 'warning',
              class: 'text-warning ft-icon--4',
              collection: 'factoricons-slim'
            },
            actions: [
              {
                label: $localize`Cancel`,
                value: 0,
                type: 'stroked',
                class: 'flex-grow-1'
              },
              {
                label: $localize`Accept`,
                value: 1,
                type: 'flat',
                class: 'flex-grow-1'
              }
            ]
          }
        )
        .subscribe(async (value: number) => {
          if (value === 1) {
            const response = await this.apiService.call({
              microServiceName: 'WSMPCreditManagement',
              resourcePath: `/payment/reverse-payment`,
              method: 'POST',
              useToken: true,
              body: {
                countryId: credit.countryId,
                productTypeId: 'CON',
                operationNumber: credit.operationNumber,
                ticketNumber: payment.ticketNumber
              },
              bodyEncrypted: true
            });
            this.change.emit({ method: 'update', entity: response.data });
            credit.amountRecoveredToDate = response.data.amountRecoveredToDate;
            credit.amountOverdueToDate = response.data.amountOverdueToDate;
            credit.interestEarnedToDate = response.data.interestEarnedToDate;
            credit.expirationDate = response.data.dueDateCredit;
            resolve({ method: 'update', entity: response.data });
            this.messageService.show(response.data.message, {
              class: 'ft-message--success',
              icon: 'check--circle'
            });
          } else {
            reject(null);
          }
        });
    });
  }

  /**
   * @description Verifica si el usuario puede crear créditos según restricciones de suscripción
   * @param {Credit} credit - Crédito a validar
   * @returns {Promise<boolean>} true si está permitido, false si se muestra mensaje de restricción
   */
  async allowNewCredit(credit: Credit): Promise<boolean> {
    const currencyId = this.storageService.get(
      this.appService.currencyId,
      'local'
    );
    if (!this.appService.restrictions()?.maxCreditAmount) {
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPApplicationSettings',
        resourcePath: `/appsrv/get-validation-rules/${currencyId}/CON/${this.sessionService.params()['profileId']}`,
        method: 'GET'
      });
      const country = response.data.find(
        (c: any) => c.countryId === credit.countryId
      );
      const maxCreditAmount = country?.validations.find(
        (r: any) => r.id === 'MPMMC'
      );
      if (maxCreditAmount) {
        this.appService.restrictions.update(r => {
          r.maxCreditAmount = Number(maxCreditAmount.value);
          return r;
        });
      }
    }
    const amountInvested =
      this.appService.restrictions()?.amountInvested?.[currencyId] ?? 0;
    const maxCreditAmount =
      this.appService.restrictions()?.maxCreditAmount ?? 0;
    const numOffersToActivate =
      this.appService.restrictions()?.numOffersToActivate ?? 0;

    if (numOffersToActivate <= 0) {
      this.messageService.show(
        $localize`You have reached the limit of active loans according to your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    } else if (amountInvested + credit.amount > maxCreditAmount) {
      this.messageService.show(
        $localize`You are exceeding the maximum credit amount you can register under your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    }
    return true;
  }
}
